import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Leaf, Sun, Wind, BellRing, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CleanEnergy() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/notify`, { email });
      setDone(true);
      toast.success("You're on the list! We'll notify you at launch.");
      setEmail("");
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.success("You're already on the list!");
        setDone(true);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="clean-energy"
      data-testid="clean-energy-section"
      className="relative py-28 md:py-36 px-6 md:px-12 lg:px-20 overflow-hidden grain"
    >
      <div className="absolute inset-0">
        <img src={IMAGES.energy} alt="Clean energy" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#050A15]/75" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#050A15] via-[#050A15]/85 to-transparent" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.7 }}
        className="relative z-10 max-w-3xl"
      >
        <div className="inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] mb-8">
          <span className="h-2 w-2 bg-[#D4AF37] rounded-full pulse-gold" /> Coming Fall 2026
        </div>

        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter leading-[0.92]">
          Clean Energy
          <br />
          <span className="gold-text">Consultation &amp; Development</span>
        </h2>

        <p className="mt-7 text-lg text-[#C9CDD9] max-w-2xl leading-relaxed">
          The next chapter of On This Rock. We're expanding our 40 years of industrial expertise into
          renewable energy — guiding businesses through clean energy strategy, compliance, and project
          development. Be the first to know when we launch.
        </p>

        <div className="mt-8 flex flex-wrap gap-4">
          {[
            { icon: Sun, label: "Solar Strategy" },
            { icon: Wind, label: "Renewable Development" },
            { icon: Leaf, label: "Compliance & Consulting" },
          ].map((f) => (
            <div key={f.label} className="inline-flex items-center gap-2 bg-[#0C142A]/70 backdrop-blur border border-[#1D2847] px-4 py-2 text-sm text-[#F8F9FA]">
              <f.icon size={16} className="text-[#D4AF37]" /> {f.label}
            </div>
          ))}
        </div>

        {done ? (
          <div data-testid="notify-success" className="mt-10 inline-flex items-center gap-3 bg-[#0C142A] border border-[#D4AF37] px-6 py-4">
            <CheckCircle2 className="text-[#D4AF37]" size={22} />
            <span className="text-[#F8F9FA] font-medium">You're on the launch list. Talk soon!</span>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3 max-w-lg">
            <input
              type="email"
              required
              data-testid="notify-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="flex-1 bg-[#131E3D] border border-[#1D2847] text-[#F8F9FA] focus:border-[#D4AF37] focus:outline-none px-4 py-3 placeholder:text-[#A3A8B8]"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="notify-submit-btn"
              className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#050A15] hover:bg-[#F0C84B] font-bold uppercase tracking-wider px-6 py-3 transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <BellRing size={18} />}
              Notify Me
            </button>
          </form>
        )}
      </motion.div>
    </section>
  );
}
